import { useId } from "react";

// Pup is the Polypup mascot - a pup reading a book, drawn after the favicon.
// It sits on a primary-colored backdrop (a rounded square for the logo, a
// circle elsewhere) so its dark ears stay visible in both themes. The "happy"
// mood closes its eyes into smiles, for celebrations.
export function Pup({
  className = "w-8 h-8",
  shape = "square",
  mood = "reading",
}: {
  // Sizing classes; the drawing scales to fill them.
  className?: string;
  shape?: "square" | "circle";
  mood?: "reading" | "happy";
}) {
  const ink = "#221f1a";
  const fur = "#fbf5e8";
  // The backdrop doubles as the clip shape, so the book never pokes out of
  // a circle. Ids must be unique per instance; useId's colons are dropped to
  // keep the url() reference plain.
  const clipId = `pup-clip${useId().replace(/:/g, "")}`;
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
      <clipPath id={clipId}>
        {shape === "square" ? (
          <rect width="64" height="64" rx="16" />
        ) : (
          <circle cx="32" cy="32" r="32" />
        )}
      </clipPath>
      <rect
        width="64"
        height="64"
        className="fill-primary"
        clipPath={`url(#${clipId})`}
      />
      {/* The clip sits on an untransformed group: on the transformed one it
          would be scaled along with the drawing. */}
      <g clipPath={`url(#${clipId})`}>
        <g
          transform={
            shape === "circle"
              ? "translate(32 35) scale(0.9) translate(-32 -33)"
              : undefined
          }
        >
          <path
            d="M19 14.5 C10.5 15.5 6.5 27 7.2 39.5 C7.6 46.5 13.6 48.5 16.8 44 C19.6 39.8 21 29 22.6 19.5 Z"
            fill={ink}
          />
          <path
            d="M45 14.5 C53.5 15.5 57.5 27 56.8 39.5 C56.4 46.5 50.4 48.5 47.2 44 C44.4 39.8 43 29 41.4 19.5 Z"
            fill={ink}
          />
          <path
            d="M32 9.5 C20.5 9.5 15.2 17 15.2 27.5 C15.2 39.5 22 46.8 32 46.8 C42 46.8 48.8 39.5 48.8 27.5 C48.8 17 43.5 9.5 32 9.5 Z"
            fill={fur}
          />
          <path
            d="M30.6 10 C30.4 7.8 31.3 6.4 33.1 5.8 M33.2 10 C34 8.2 35.4 7.5 37 7.7"
            stroke={ink}
            strokeWidth="1.5"
            strokeLinecap="round"
            fill="none"
          />
          {mood === "happy" ? (
            <path
              d="M23 28.6 Q25.4 25.6 27.8 28.6 M36.2 28.6 Q38.6 25.6 41 28.6"
              stroke={ink}
              strokeWidth="1.9"
              strokeLinecap="round"
              fill="none"
            />
          ) : (
            <>
              <ellipse cx="25.4" cy="28.2" rx="2.3" ry="2.9" fill={ink} />
              <ellipse cx="38.6" cy="28.2" rx="2.3" ry="2.9" fill={ink} />
              <circle cx="26.1" cy="27.2" r="0.75" fill="#ffffff" />
              <circle cx="39.3" cy="27.2" r="0.75" fill="#ffffff" />
            </>
          )}
          <ellipse
            cx="21.6"
            cy="34.2"
            rx="2.6"
            ry="1.6"
            fill="#f2a593"
            opacity="0.45"
          />
          <ellipse
            cx="42.4"
            cy="34.2"
            rx="2.6"
            ry="1.6"
            fill="#f2a593"
            opacity="0.45"
          />
          <path
            d="M27.8 34.8 C27.8 32.7 36.2 32.7 36.2 34.8 C36.2 37.3 33.7 38.8 32 38.8 C30.3 38.8 27.8 37.3 27.8 34.8 Z"
            fill={ink}
          />
          <ellipse
            cx="30.7"
            cy="34.3"
            rx="1.35"
            ry="0.65"
            fill="#ffffff"
            opacity="0.7"
          />
          <path
            d="M5 45 C13.5 41.5 23.5 41.5 32 46 C40.5 41.5 50.5 41.5 59 45 L59 60 C50.5 56.5 40.5 56.5 32 61 C23.5 56.5 13.5 56.5 5 60 Z"
            fill="#8a5332"
          />
          <path
            d="M7.5 45.1 C15 42.7 24 42.9 31 46.7 M56.5 45.1 C49 42.7 40 42.9 33 46.7"
            stroke={fur}
            strokeWidth="1.4"
            fill="none"
            strokeLinecap="round"
          />
          <path d="M32 46.3 L32 60.6" stroke="#5c341d" strokeWidth="1.4" />
          <ellipse cx="10.2" cy="48.6" rx="3.4" ry="3.9" fill={fur} />
          <ellipse cx="53.8" cy="48.6" rx="3.4" ry="3.9" fill={fur} />
        </g>
      </g>
    </svg>
  );
}
